// RapportsPOC.js — Documentation Fonctionnelle : POC « Rapports » (module Campagne).
// Aperçu interactif de l'écran réel du Centre de Rapports : en-tête + actions de génération,
// cartes KPI, onglets (Tous / Campagnes / Exécutions / Archives), barre de filtres (recherche,
// période, tri, format), tableau des rapports (sélection multiple, actions par ligne, pagination)
// et aperçu de la synthèse d'un rapport généré (KPIs, répartition, verdict, export).
// Source de vérité : automata-studio-frontend [CPG][06]-Rapports.js + automata_campaign ReportController / PdfReportGenerator.
import React, { useState } from 'react';
import {
  FileText, FileSpreadsheet, Download, CloudDownload, Sparkles, Target, Activity,
  Clock, CheckCircle, XCircle, AlertTriangle, Info, ArrowRight, BarChart3, Timer, RefreshCw,
  ListChecks, TrendingUp, Layers, ShieldCheck, Filter, Archive, ArchiveRestore,
  Trash2, Eye, X, Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Formats supportés par le backend (HC-01 : seuls PDF et EXCEL).
const FORMATS = [
  { value: 'PDF', label: 'PDF', icon: FileText, color: '#EF4444', description: 'Document portable', ext: '.pdf' },
  { value: 'EXCEL', label: 'Excel', icon: FileSpreadsheet, color: '#10B981', description: 'Feuille de calcul', ext: '.xlsx' },
];

// Sources disponibles (campagnes + exécutions) — données en dur, miroir de l'écran réel.
const SOURCES = [
  { id: 'cmp-1', kind: 'campaign', name: 'Recette CBPR+ Q2', code: 'CMP-2026-014', period: 'Avr. → Juin 2026', total: 48, passed: 45, failed: 2, error: 1, skipped: 0, durationMs: 412000, execs: 6, critical: 12 },
  { id: 'cmp-2', kind: 'campaign', name: 'Non-régression Core', code: 'CMP-2026-021', period: 'Mai 2026', total: 120, passed: 118, failed: 1, error: 0, skipped: 1, durationMs: 1380000, execs: 9, critical: 30 },
  { id: 'exe-1', kind: 'execution', name: 'Recette CBPR+ Q2 — Run #18', code: 'EXE-2026-0188', period: '14/06/2026 09:12', total: 48, passed: 47, failed: 1, error: 0, skipped: 0, durationMs: 68000, execs: 1, critical: 12 },
  { id: 'exe-2', kind: 'execution', name: 'Smoke RTGS — Run #07', code: 'EXE-2026-0207', period: '13/06/2026 18:40', total: 22, passed: 16, failed: 4, error: 2, skipped: 0, durationMs: 51000, execs: 1, critical: 5 },
];

// Rapports déjà générés (tableau principal) — données en dur, miroir de l'écran réel.
const SAMPLE_REPORTS = [
  { reportId: 'RPT-2026-0451', name: 'Rapport Exécution — Recette CBPR+ Q2 Run #18', kind: 'execution', sourceName: 'Recette CBPR+ Q2 — Run #18', sourceCode: 'EXE-2026-0188', format: 'PDF', date: '14/06/2026 09:15', by: 'admin', sizeKb: 312, archived: false },
  { reportId: 'RPT-2026-0449', name: 'Rapport Campagne — Non-régression Core', kind: 'campaign', sourceName: 'Non-régression Core', sourceCode: 'CMP-2026-021', format: 'EXCEL', date: '13/06/2026 22:03', by: 'qa.lead', sizeKb: 88, archived: false },
  { reportId: 'RPT-2026-0447', name: 'Rapport Exécution — Smoke RTGS Run #07', kind: 'execution', sourceName: 'Smoke RTGS — Run #07', sourceCode: 'EXE-2026-0207', format: 'PDF', date: '13/06/2026 18:45', by: 'ops', sizeKb: 196, archived: false },
  { reportId: 'RPT-2026-0440', name: 'Rapport Campagne — Recette CBPR+ Q2', kind: 'campaign', sourceName: 'Recette CBPR+ Q2', sourceCode: 'CMP-2026-014', format: 'PDF', date: '10/06/2026 11:20', by: 'admin', sizeKb: 274, archived: true },
];

const kindLabel = (k) => (k === 'execution' ? 'Exécution' : 'Campagne');
const kindColor = (k) => (k === 'execution' ? '#0EA5E9' : '#8B5CF6');
const formatOf = (v) => FORMATS.find((f) => f.value === v) || FORMATS[0];
const fmtDuration = (ms) => {
  const s = Math.round(ms / 1000); const m = Math.floor(s / 60); const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};
const rate = (s) => (s.total > 0 ? (s.passed / s.total) * 100 : 0);

// ---- Petits sous-composants de la réplique ---------------------------------
const KpiCard = ({ icon: IconCmp, label, value, gradient }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-xl p-4 text-white shadow-sm`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-white/80 text-xs mt-1">{label}</p>
      </div>
      <span className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center"><IconCmp className="h-6 w-6" /></span>
    </div>
  </div>
);

const SynthCard = ({ icon: IconCmp, label, value, accent = '#3B82F6' }) => (
  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><IconCmp className="h-4 w-4" style={{ color: accent }} />{label}</div>
    <div className="text-xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const RapportsPOC = () => {
  const [tab, setTab] = useState('all'); // 'all' | 'campaign' | 'execution' | 'archived'
  const [search, setSearch] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [sort, setSort] = useState('date-desc'); // date-desc | date-asc | name-asc | name-desc
  const [formatFilter, setFormatFilter] = useState(''); // '' | 'PDF' | 'EXCEL'
  const [sourceId, setSourceId] = useState(SOURCES[0].id);
  const [genFormat, setGenFormat] = useState('PDF');
  const [reports, setReports] = useState(SAMPLE_REPORTS);
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null); // synthèse simulée du dernier rapport
  const [exported, setExported] = useState(null); // { format } — confirmation d'export simulé
  const [notice, setNotice] = useState(null); // message d'action simulé

  const source = SOURCES.find((s) => s.id === sourceId) || SOURCES[0];

  // Statistiques (cartes KPI + compteurs d'onglets), calculées sur la liste en dur.
  const stats = {
    total: reports.length,
    campaign: reports.filter((r) => r.kind === 'campaign').length,
    execution: reports.filter((r) => r.kind === 'execution').length,
    archived: reports.filter((r) => r.archived).length,
    byFormat: FORMATS.reduce((acc, f) => ({ ...acc, [f.value]: reports.filter((r) => r.format === f.value).length }), {}),
  };

  // Filtrage + tri du tableau (miroir de la logique réelle).
  const visibleReports = reports
    .filter((r) => `${r.name} ${r.reportId} ${r.by}`.toLowerCase().includes(search.toLowerCase()))
    .filter((r) => !formatFilter || r.format === formatFilter)
    .filter((r) => {
      if (tab === 'campaign') return r.kind === 'campaign' && !r.archived;
      if (tab === 'execution') return r.kind === 'execution' && !r.archived;
      if (tab === 'archived') return r.archived;
      return !r.archived; // 'all' : actifs uniquement
    })
    .sort((a, b) => {
      if (sort === 'name-asc') return a.name.localeCompare(b.name);
      if (sort === 'name-desc') return b.name.localeCompare(a.name);
      if (sort === 'date-asc') return a.date.localeCompare(b.date);
      return b.date.localeCompare(a.date);
    });

  const allSelected = visibleReports.length > 0 && visibleReports.every((r) => selected.includes(r.reportId));

  // Génération SIMULÉE : construit la synthèse à partir de la source choisie et l'ajoute au tableau.
  const generate = () => {
    setGenerating(true); setExported(null); setNotice(null);
    window.setTimeout(() => {
      const r = rate(source);
      const reportId = `RPT-2026-${Math.floor(500 + Math.random() * 499)}`;
      const synth = {
        reportId, name: `Rapport ${kindLabel(source.kind)} — ${source.name}`, kind: source.kind,
        sourceName: source.name, sourceCode: source.code, format: genFormat, date: '16/06/2026 10:30',
        by: 'vous', sizeKb: genFormat === 'PDF' ? 248 : 74, archived: false,
        synthesis: {
          total: source.total, passed: source.passed, failed: source.failed, error: source.error,
          skipped: source.skipped, durationMs: source.durationMs, execs: source.execs, critical: source.critical,
          rate: r, verdict: source.failed === 0 && source.error === 0 ? 'GO' : 'NO-GO',
        },
      };
      setReports((prev) => [{ ...synth, synthesis: undefined }, ...prev]);
      setPreview(synth);
      setGenerating(false);
    }, 750);
  };

  const exportAs = (fmt) => setExported({ format: fmt });
  const pickSource = (id) => { setSourceId(id); setPreview(null); setExported(null); };
  const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleArchive = (id) => {
    setReports((p) => p.map((r) => (r.reportId === id ? { ...r, archived: !r.archived } : r)));
    setNotice('Statut d\'archivage mis à jour (démo).');
  };
  const removeReport = (id) => {
    setReports((p) => p.filter((r) => r.reportId !== id));
    setSelected((p) => p.filter((x) => x !== id));
    setNotice('Rapport supprimé (démo).');
  };

  return (
    <div className="space-y-8">
      {/* Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Rapports</h1>
          <Badge color="#F59E0B">Module Campagne</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran du <strong>Centre de Rapports</strong> (démo, actions simulées localement).</p>
      </div>

      {/* ===== Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app + actions de génération */}
        <div className="px-5 py-4 bg-gradient-to-r from-violet-600 to-purple-700 text-white flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><FileText className="h-5 w-5" /></span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold leading-tight">Centre de Rapports</h2>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-white/20">{stats.total}</span>
              </div>
              <p className="text-xs text-white/80">Générez et gérez vos rapports de campagnes et d'exécutions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={generate} disabled={generating} className="bg-white/20 hover:bg-white/30 text-white"><Target className="h-4 w-4" />Rapport Campagne</Button>
            <Button onClick={generate} disabled={generating} className="bg-white/20 hover:bg-white/30 text-white"><Activity className="h-4 w-4" />Rapport Exécution</Button>
          </div>
        </div>

        {/* Cartes KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <KpiCard icon={FileText} label="Total rapports" value={stats.total} gradient="from-violet-500 to-purple-700" />
          <KpiCard icon={Target} label="Rapports campagne" value={stats.campaign} gradient="from-purple-500 to-fuchsia-700" />
          <KpiCard icon={Activity} label="Rapports exécution" value={stats.execution} gradient="from-teal-500 to-cyan-700" />
          <KpiCard icon={Archive} label="Rapports archivés" value={stats.archived} gradient="from-gray-500 to-gray-700" />
        </div>

        {/* Onglets */}
        <div className="px-4 pt-3">
          <Tabs
            tabs={[
              { id: 'all', label: 'Tous les rapports', count: stats.total },
              { id: 'campaign', label: 'Campagnes', count: stats.campaign },
              { id: 'execution', label: 'Exécutions', count: stats.execution },
              { id: 'archived', label: 'Archives', count: stats.archived },
            ]}
            active={tab} onChange={(t) => { setTab(t); setSelected([]); }}
          />
        </div>

        {/* Barre de filtres et recherche */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-violet-500" /><span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Filtres et recherche</span></div>
            <Button variant="ghost" size="sm" onClick={() => setNotice('Liste actualisée (démo).')}><RefreshCw className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SearchBar value={search} onChange={setSearch} placeholder="Rechercher des rapports…" className="flex-1 min-w-[200px] max-w-sm" />
            <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} aria-label="Date de début"
              className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} aria-label="Date de fin"
              className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Trier"
              className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="date-desc">Plus récent</option>
              <option value="date-asc">Plus ancien</option>
              <option value="name-asc">Nom A-Z</option>
              <option value="name-desc">Nom Z-A</option>
            </select>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Format :</span>
            <button onClick={() => setFormatFilter('')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${formatFilter === '' ? 'bg-violet-500 text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>Tous</button>
            {FORMATS.map((f) => {
              const on = formatFilter === f.value;
              return (
                <button key={f.value} onClick={() => setFormatFilter(on ? '' : f.value)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${on ? 'bg-violet-500 text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                  <f.icon className="h-3 w-3" />{f.label}
                  {stats.byFormat[f.value] > 0 && <span className={`ml-0.5 text-[10px] ${on ? 'text-white/80' : 'text-gray-400'}`}>({stats.byFormat[f.value]})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bandeau sélection multiple */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
              {tab === 'all' && 'Tous les rapports'}{tab === 'campaign' && 'Rapports de campagne'}{tab === 'execution' && "Rapports d'exécution"}{tab === 'archived' && 'Rapports archivés'}
            </h3>
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">{visibleReports.length}</span>
            {selected.length > 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 flex items-center gap-1">
                {selected.length} sélectionné(s)
                <button onClick={() => setSelected([])} className="hover:text-violet-900 dark:hover:text-white"><X className="h-3 w-3" /></button>
              </span>
            )}
          </div>
          {selected.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => { setNotice(`${selected.length} rapport(s) téléchargé(s) (démo).`); setSelected([]); }}>
              <Download className="h-4 w-4" />Télécharger ({selected.length})
            </Button>
          )}
        </div>

        {/* Tableau des rapports */}
        <div className="overflow-x-auto">
          {visibleReports.length === 0 ? (
            <EmptyState icon={FileText} title="Aucun rapport trouvé" message="Aucun rapport ne correspond à vos critères. Générez votre premier rapport ou modifiez les filtres." />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="w-10 py-3 px-4">
                    <input type="checkbox" checked={allSelected}
                      onChange={(e) => setSelected(e.target.checked ? visibleReports.map((r) => r.reportId) : [])}
                      className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                  </th>
                  {['Rapport', 'Type', 'Source', 'Format', 'Date', 'Statut'].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleReports.map((r) => {
                  const fc = formatOf(r.format);
                  return (
                    <tr key={r.reportId} className={`border-b border-gray-100 dark:border-gray-700/50 transition-colors ${selected.includes(r.reportId) ? 'bg-violet-50/50 dark:bg-violet-900/10' : 'hover:bg-violet-50/30 dark:hover:bg-violet-900/5'}`}>
                      <td className="py-3 px-4">
                        <input type="checkbox" checked={selected.includes(r.reportId)} onChange={() => toggleSelect(r.reportId)}
                          className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${fc.color}20` }}><fc.icon className="h-4 w-4" style={{ color: fc.color }} /></span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate max-w-[220px]">{r.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{r.reportId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4"><Badge color={kindColor(r.kind)}>{kindLabel(r.kind)}</Badge></td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-800 dark:text-gray-200">{r.sourceName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{r.sourceCode}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border" style={{ color: fc.color, borderColor: `${fc.color}60` }}><fc.icon className="h-3 w-3" />{r.format}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-800 dark:text-gray-200">{r.date}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">par {r.by}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${r.archived ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
                          {r.archived ? <Archive className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}{r.archived ? 'Archivé' : 'Actif'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setNotice(`Rapport « ${r.name} » téléchargé (démo).`)} title="Télécharger" className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"><CloudDownload className="h-4 w-4" /></button>
                          <button onClick={() => setNotice(`Aperçu de « ${r.name} » (démo).`)} title="Prévisualiser" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"><Eye className="h-4 w-4" /></button>
                          <button onClick={() => toggleArchive(r.reportId)} title={r.archived ? 'Désarchiver' : 'Archiver'}
                            className={`p-1.5 rounded-lg transition-colors ${r.archived ? 'text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}>
                            {r.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                          </button>
                          {r.archived && (
                            <button onClick={() => removeReport(r.reportId)} title="Supprimer" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"><Trash2 className="h-4 w-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination (statique, miroir de l'écran réel) */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Lignes par page :</span>
            <select defaultValue="25" className="text-xs rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-400">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">{visibleReports.length > 0 ? `1-${visibleReports.length} sur ${visibleReports.length}` : '0 sur 0'}</span>
          <div className="flex gap-1">
            <button disabled className="px-2 py-1 rounded-md text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">Préc.</button>
            <button disabled className="px-2 py-1 rounded-md text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">Suiv.</button>
          </div>
        </div>

        {notice && (
          <div className="mx-4 mb-4 flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-300 text-sm">
            <Info className="h-4 w-4 shrink-0" />{notice}
          </div>
        )}
      </Card>

      {/* ===== Génération + aperçu de la synthèse (fenêtre simulée) ===== */}
      <Card className="overflow-hidden">
        <CardHeader className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-500" />Générer un rapport</CardTitle>
          <Badge color="#3B82F6">Démo</Badge>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Configuration de génération */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-3"><Info className="h-4 w-4 text-violet-500" /><span className="text-sm font-semibold text-gray-900 dark:text-white">Paramètres du rapport</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1">Source <span className="text-red-500">*</span></span>
                <select value={sourceId} onChange={(e) => pickSource(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500">
                  {SOURCES.map((s) => <option key={s.id} value={s.id}>{kindLabel(s.kind)} — {s.name} ({s.code})</option>)}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1">Période</span>
                <input value={source.period} readOnly className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 rounded-md" />
              </label>
            </div>
            <div className="mt-4">
              <span className="block text-sm text-gray-700 dark:text-gray-300 mb-1.5">Format <span className="text-red-500">*</span></span>
              <div className="flex gap-2 flex-wrap">
                {FORMATS.map((f) => {
                  const on = genFormat === f.value;
                  return (
                    <button key={f.value} onClick={() => setGenFormat(f.value)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${on ? 'text-white shadow-sm' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600'}`}
                      style={on ? { backgroundColor: f.color, borderColor: f.color } : undefined}>
                      <f.icon className="h-4 w-4" />{f.label}<span className={`text-xs ${on ? 'text-white/80' : 'text-gray-400'}`}>{f.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={generate} disabled={generating} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white">
                {generating ? <>Génération…</> : <><Sparkles className="h-4 w-4" />Générer le rapport</>}
              </Button>
            </div>
          </div>

          {/* Aperçu de la synthèse */}
          {generating ? (
            <div className="flex flex-col items-center justify-center h-56 text-gray-400">
              <BarChart3 className="h-10 w-10 mb-3 animate-pulse text-violet-400" />
              <p className="text-sm">Préparation de la synthèse du rapport…</p>
            </div>
          ) : preview ? (
            <div>
              {/* En-tête aperçu */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${formatOf(preview.format).color}20` }}>
                    {(() => { const Fi = formatOf(preview.format).icon; return <Fi className="h-6 w-6" style={{ color: formatOf(preview.format).color }} />; })()}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">{preview.name}</h3>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{preview.reportId} • {kindLabel(preview.kind)} • généré par {preview.by}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge color={preview.synthesis.verdict === 'GO' ? '#10B981' : '#EF4444'}>
                    {preview.synthesis.verdict === 'GO' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{preview.synthesis.verdict}
                  </Badge>
                  <Badge color={formatOf(preview.format).color}>{preview.format}</Badge>
                </div>
              </div>

              {/* Bandeau verdict */}
              <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 border ${preview.synthesis.verdict === 'GO' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300'}`}>
                {preview.synthesis.verdict === 'GO' ? <ShieldCheck className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                <span className="text-sm font-medium">{preview.synthesis.verdict === 'GO' ? 'GO — Tous les tests sont passés avec succès' : 'NO-GO — Des tests sont en échec ou en erreur'}</span>
              </div>

              {/* Synthèse : KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <SynthCard icon={ListChecks} label="Cas de test" value={preview.synthesis.total} accent="#3B82F6" />
                <SynthCard icon={CheckCircle} label="Réussis" value={preview.synthesis.passed} accent="#10B981" />
                <SynthCard icon={XCircle} label="Échoués" value={preview.synthesis.failed + preview.synthesis.error} accent="#EF4444" />
                <SynthCard icon={TrendingUp} label="Taux de réussite" value={`${preview.synthesis.rate.toFixed(1)}%`} accent={preview.synthesis.rate >= 80 ? '#10B981' : preview.synthesis.rate >= 50 ? '#F59E0B' : '#EF4444'} />
              </div>

              {/* Barre de répartition */}
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Répartition des statuts</div>
                <div className="flex h-6 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
                  {[['#10B981', preview.synthesis.passed], ['#EF4444', preview.synthesis.failed], ['#B71C1C', preview.synthesis.error], ['#F59E0B', preview.synthesis.skipped]].map(([c, n], i) =>
                    n > 0 ? <div key={i} style={{ backgroundColor: c, width: `${(n / preview.synthesis.total) * 100}%` }} title={`${n}`} /> : null
                  )}
                </div>
                <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Réussis {preview.synthesis.passed}</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Échoués {preview.synthesis.failed}</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#B71C1C' }} />Erreur {preview.synthesis.error}</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Ignorés {preview.synthesis.skipped}</span>
                </div>
              </div>

              {/* Lignes complémentaires */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60 mb-4">
                {[[Timer, 'Durée totale', fmtDuration(preview.synthesis.durationMs)], [Layers, 'Exécutions couvertes', preview.synthesis.execs], [AlertTriangle, 'Tests critiques', preview.synthesis.critical], [Clock, 'Date de génération', preview.date]].map(([Ic, l, v], i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><Ic className="h-4 w-4" />{l}</span>
                    <span className="text-gray-800 dark:text-gray-200 font-medium">{v}</span>
                  </div>
                ))}
              </div>

              {/* Export simulé */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600 dark:text-gray-300 mr-1">Exporter :</span>
                <Button variant="outline" size="sm" onClick={() => exportAs('PDF')}><FileText className="h-4 w-4 text-red-500" />PDF</Button>
                <Button variant="outline" size="sm" onClick={() => exportAs('EXCEL')}><FileSpreadsheet className="h-4 w-4 text-emerald-500" />Excel</Button>
                <Button variant="ghost" size="sm" onClick={() => exportAs(preview.format)}><Download className="h-4 w-4" />Télécharger ({preview.format})</Button>
                <Button variant="ghost" size="sm" onClick={() => setNotice(`ID « ${preview.reportId} » copié (démo).`)}><Copy className="h-4 w-4" />Copier ID</Button>
              </div>
              {exported && (
                <div className="mt-3 flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
                  <CheckCircle className="h-4 w-4" />Export {exported.format} simulé — fichier « {preview.name}{formatOf(exported.format).ext} » prêt (démo, aucun téléchargement réel).
                </div>
              )}
            </div>
          ) : (
            <EmptyState icon={BarChart3} title="Aucun aperçu" message="Sélectionnez une source, choisissez un format puis cliquez sur « Générer le rapport » pour afficher la synthèse." />
          )}
        </CardContent>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-violet-500" />Choisir une source</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Filtrer avec les onglets <strong>Campagnes</strong>, <strong>Exécutions</strong> ou <strong>Archives</strong>. <button onClick={() => setTab('campaign')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Affiner avec la <strong>recherche</strong>, la <strong>période</strong>, le <strong>tri</strong> ou le <strong>format</strong>.</GuideStep>
              <GuideStep n={3}>Choisir la <strong>source</strong> à rapporter dans la fenêtre de génération ci-dessous.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Sparkles className="h-5 w-5 text-violet-500" />Générer un rapport</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Choisir le <strong>format</strong> : <strong>PDF</strong> (synthèse mise en page) ou <strong>Excel</strong> (données). <button onClick={() => setGenFormat('PDF')} className="text-blue-600 dark:text-blue-400 underline">PDF</button></GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Générer le rapport »</strong>. <button onClick={generate} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Consulter l'<strong>aperçu</strong> : verdict GO / NO-GO, KPIs, répartition, durée et taux.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Download className="h-5 w-5 text-violet-500" />Exporter / Gérer</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Dans l'aperçu, utiliser <strong>Exporter PDF</strong> ou <strong>Exporter Excel</strong>. <button onClick={() => { if (!preview) generate(); window.setTimeout(() => exportAs('PDF'), 800); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Depuis le tableau : <strong>télécharger</strong>, <strong>prévisualiser</strong>, <strong>archiver</strong> ou <strong>supprimer</strong> (si archivé).</GuideStep>
              <GuideStep n={3}>Le rapport généré s'ajoute en haut du <strong>tableau des rapports</strong>.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence : sections du rapport ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-violet-500" />Sections du rapport généré</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Section', 'Contenu', 'Exécution', 'Campagne'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Page de garde', 'Type, titre, objectif, date et auteur', 'Oui', 'Oui'],
                ['Synthèse exécutive', 'KPIs : total, réussis, échoués, taux de réussite', 'Oui', '—'],
                ['Vue d\'ensemble', 'Statut, priorité, environnements, indicateurs clés', '—', 'Oui'],
                ['Répartition des statuts', 'Barre + camembert (réussis / échoués / ignorés / erreur)', 'Oui', 'Oui'],
                ['Détail des résultats', 'Tableau par cas : statut, durée, attendu / obtenu', 'Oui', '—'],
                ['Inventaire des cas', 'Liste des cas de test, criticité, préconditions', '—', 'Oui'],
                ['Analyse de performance', 'Durée totale / moyenne / min / max, top 5 lents', 'Oui', '—'],
                ['Historique des exécutions', 'Exécutions, statuts, taux + statistiques agrégées', '—', 'Oui'],
                ['Anomalies et erreurs', 'Échecs détaillés : message, stack trace, logs', 'Oui', '—'],
                ['Conclusion', 'Verdict GO / NO-GO + résumé (taux, durée)', 'Oui', 'Oui'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#0EA5E9">Oui</Badge> : <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2">{r[3] === 'Oui' ? <Badge color="#8B5CF6">Oui</Badge> : <span className="text-gray-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Une <strong>source</strong> (campagne ou exécution) et un <strong>format</strong> sont obligatoires pour générer.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Seuls les formats <strong>PDF</strong> et <strong>Excel</strong> sont supportés par le backend.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le verdict <strong>GO</strong> exige 0 test en échec et 0 en erreur ; sinon <strong>NO-GO</strong>.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un rapport doit être <strong>archivé</strong> avant de pouvoir être supprimé.</li>
            <li className="flex items-start gap-2"><Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />Le taux de réussite = réussis / total ; ≥ 80% (vert), ≥ 50% (orange), sinon rouge.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['POST', '#3B82F6', '/api/automata/reports/executions/{id}?format='],
              ['POST', '#3B82F6', '/api/automata/reports/campaigns/{id}?format='],
              ['GET', '#10B981', '/api/automata/reports'],
              ['GET', '#10B981', '/api/automata/reports/{id}'],
              ['GET', '#10B981', '/api/automata/reports/{id}/download'],
              ['PATCH', '#8B5CF6', '/api/automata/reports/{id}/archive'],
              ['DELETE', '#EF4444', '/api/automata/reports/{id}'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_campaign / ReportController + PdfReportGenerator</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default RapportsPOC;
